import React, { useContext } from "react";
import { Layout, Button, Popover, Avatar, Popconfirm, Select } from "antd";
import { useNavigate, useSubmit } from "react-router-dom";
import { auth } from "~/lib/firebase";

import {
  UserOutlined,
  InboxOutlined,
  QuestionCircleOutlined,
  GlobalOutlined,
} from "@ant-design/icons";
import { useMediaQuery } from "@mui/material";
import Cookies from "js-cookie";
import { ROUTES } from "~/utils";
import { useAppTranslation } from "~/hooks/useAppTranslation";
import { userContext } from "~/context/ContextProvider";
import i18n from "~/i18n";

const { Header } = Layout;

export const TopNavigation = () => {
  const isMobile = useMediaQuery("(max-width: 600px)");
  const navigate = useNavigate();
  const { profile } = useContext(userContext);
  const [showSignoutConfirm, setShowSignoutConfirm] = React.useState(false);
  const { t, i18n: i18nInstance } = useAppTranslation();
  const submit = useSubmit();
  const toggleSignoutConfirm = () => setShowSignoutConfirm(!showSignoutConfirm);

  const displayName =
    [profile?.firstName, profile?.lastName].filter(Boolean).join(" ") ||
    profile?.username ||
    auth.currentUser?.displayName ||
    "";
  const displayEmail = profile?.email ?? auth.currentUser?.email ?? "";
  const avatarSeed = profile?.username || auth.currentUser?.email || "tenant";

  const handleLanguageChange = (newLanguage: string) => {
    Cookies.set("language", newLanguage);
    i18n.changeLanguage(newLanguage);
    submit({ method: "get" });
  };

  const handleLogout = async () => {
    try {
      await auth.signOut();
      navigate("/");
    } catch (error) {
      console.error("Error occurred during logout:", error);
    }
  };

  const handleMenuClick = ({ key }: { key: string }) => {
    if (key === "account") {
      navigate("/account");
    } else if (key === "settings") {
      navigate("/settings");
    } else if (key === "signout") {
      handleLogout();
    } else if (key === "help") {
      navigate("/help");
    }
  };

  const userContent = (
    <div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        {displayName || displayEmail ? (
          <Avatar
            size={64}
            src={
              profile?.profilePictureUrl ??
              `https://robohash.org/${avatarSeed}?size=200x200&bgset=bg2`
            }
            alt={displayName || displayEmail}
          />
        ) : (
          <Avatar size={64} icon={<UserOutlined />} />
        )}
        <div style={{ marginTop: "0.5rem", fontWeight: "bold" }}>{displayName}</div>
        <div style={{ marginTop: "0.5rem" }}>{displayEmail}</div>
      </div>
      <div style={{ marginTop: "1rem" }}>
        <Popconfirm
          title={"Confirm sign out action"}
          onConfirm={() => handleMenuClick({ key: "signout" })}
          onCancel={toggleSignoutConfirm}
          open={showSignoutConfirm}
        >
          <Button
            type="link"
            icon={<UserOutlined />}
            onClick={toggleSignoutConfirm}
          >
            {t("Sign_out")}
          </Button>
        </Popconfirm>
        <Button
          type="link"
          icon={<QuestionCircleOutlined />}
          onClick={() => handleMenuClick({ key: "help" })}
        >
          {t("Help")}
        </Button>
      </div>
    </div>
  );

  return (
    <Header
      style={{
        background: "#fff",
        padding: 0,
        display: "flex",
        alignItems: "center",
        flexShrink: 0,
        zIndex: 100,
        overflowX: "auto",
        overflowY: "hidden",
        scrollbarWidth: "thin",
        height: isMobile ? 64 : undefined,
        minHeight: 64,
      }}
    >
      <div
        className="logo"
        style={{
          height: isMobile ? 48 : 100,
          width: isMobile ? 48 : 60,
          minWidth: isMobile ? 48 : 60,
          paddingLeft: "0.5rem",
          flexShrink: 0,
        }}
      >
        <img
          style={{
            height: "100%",
            width: "100%",
            objectFit: "contain",
            cursor: "pointer",
          }}
          src="/logo1.png"
          alt="Bomani"
          onClick={() => navigate("/")}
        />
      </div>
      <Select
        style={{
          marginLeft: "0.5rem",
          minWidth: isMobile ? 72 : 100,
          flexShrink: 0,
        }}
        value={i18nInstance.language}
        onChange={(value) => {
          handleLanguageChange(value);
        }}
        suffixIcon={isMobile ? <GlobalOutlined /> : undefined}
        options={
          isMobile
            ? [
                { label: "EN", value: "eng" },
                { label: "FR", value: "fr" },
                { label: "ES", value: "es" },
              ]
            : [
                { label: "English", value: "eng" },
                { label: "Français", value: "fr" },
                { label: "Español", value: "es" },
              ]
        }
      />
      <div style={{ flex: 1, minWidth: 8 }} />
      <div
        style={{
          display: "flex",
          alignItems: "center",
          paddingRight: "1rem",
          flexShrink: 0,
        }}
      >
        <Button
          type="primary"
          icon={<InboxOutlined />}
          style={{ marginRight: "0.5rem" }}
          onClick={() => navigate(ROUTES.messageTablePage)}
        />
        <Popover placement="bottomRight" content={userContent} trigger="click">
          <Button
            type="primary"
            icon={<UserOutlined />}
            style={{ marginRight: "0.5rem" }}
          />
        </Popover>
      </div>
    </Header>
  );
};
